<script>
import { pricify } from '@utils/currency'

import BillItem from '@components/bill-item.vue'

export default {
  components: { BillItem },
  props: {
    checkNumber: {
      type: Number,
      default: null,
    },
    collapsable: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    selectedTotal: {
      type: Number,
      default: null,
    },
    serviceCharge: {
      type: Number,
      default: null,
    },
    splitBill: {
      type: Boolean,
      default: false,
    },
    splitServiceCharge: {
      type: Number,
      default: null,
    },
    restaurant: {
      type: String,
      default: null,
    },
    tableNumber: {
      type: Number,
      default: null,
    },
    tip: {
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: '',
    },
    total: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    expanded: false,
  }),
  computed: {
    computedItems() {
      return this.items.map((item, index) => ({
        ...item,
        name: item?.display?.name,
        price: item?.tariffPrice,
        key: `${item?.display?.name}-${index}`,
      }))
    },
    computedServiceCharge() {
      return pricify(this.splitServiceCharge || this.serviceCharge)
    },
    computedSubtotal() {
      const total = parseFloat(this.selectedTotal || this.total)
      const serviceCharge = parseFloat(this.computedServiceCharge || 0)
      const tip = parseFloat(this.tip || 0)
      const subtotal = total + serviceCharge + tip
      if (subtotal === total) return null
      return pricify(subtotal)
    },
    computedTip() {
      return pricify(this.tip)
    },
    computedTotal() {
      return pricify(this.selectedTotal || this.total)
    },
  },
  methods: {
    handleExpand() {
      this.expanded = !this.expanded
    },
  },
}
</script>

<template>
  <div :class="[$style.billContainer, { [$style.collapsable]: collapsable }]">
    <div
      v-if="title || collapsable"
      :class="$style.header"
      @click="handleExpand"
    >
      <h2 v-if="title" :class="$style.title" v-text="title" />
      <div
        v-if="collapsable"
        :class="[$style.cross, { [$style.open]: expanded }]"
      />
    </div>

    <Transition name="slide">
      <div
        v-if="items.length > 0"
        v-show="!collapsable || expanded"
        :class="$style.items"
      >
        <!-- Information -->
        <div v-if="restaurant || tableNumber" :class="$style.section">
          <div v-if="restaurant" ref="restaurant" :class="$style.article">
            <div :class="[$style.flexrow, $style.row]">
              <h4 :class="$style.subtitle">Restaurant</h4>
              <h4>{{ restaurant }}</h4>
            </div>
          </div>
          <div v-if="tableNumber" ref="tableNumber" :class="$style.article">
            <div :class="[$style.flexrow, $style.row]">
              <h4 :class="$style.subtitle">Table</h4>
              <h4>#{{ tableNumber }}</h4>
            </div>
          </div>
        </div>

        <!-- Mains -->
        <div :class="$style.section">
          <div :class="[$style.article, $style.mains]">
            <BillItem
              v-for="(item, index) in computedItems"
              :key="item.key"
              :index="index"
              :selected="selectedItems && selectedItems.includes(index)"
              :price="item.price"
              :quantity="item.quantity"
              :name="item.name"
              :choices="item.choices"
              :split-bill="splitBill"
              @on-check="(item) => $emit('selected-item', item)"
            />
          </div>
        </div>

        <!-- Totals -->
        <div :class="$style.section">
          <!-- Item total -->
          <div v-if="total" :class="[$style.flexrow, $style.row, $style.bold]">
            <h4>Item total</h4>
            <h4>£{{ computedTotal }}</h4>
          </div>

          <!-- Service charge -->
          <div
            v-if="serviceCharge"
            :class="[$style.flexrow, $style.row, $style.bold]"
          >
            <h4>Service charge</h4>
            <h4>£{{ computedServiceCharge }}</h4>
          </div>

          <!-- Tip -->
          <div v-if="tip" :class="[$style.flexrow, $style.row, $style.bold]">
            <h4>Tip</h4>
            <h4>£{{ computedTip }}</h4>
          </div>

          <!-- Subtotal -->
          <div
            v-if="computedSubtotal"
            :class="[$style.flexrow, $style.row, $style.bold]"
          >
            <h4>Subtotal</h4>
            <h4>£{{ computedSubtotal }}</h4>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.billContainer {
  width: 100%;
}

.section {
  width: 100%;
  margin: 0 0 16px;
  padding: 0 0 16px;
}

.article {
  margin: 0 0 24px;

  &:last-child {
    margin: 0;
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 6px 0;
  margin-bottom: 16px;
}

.collapsable .header {
  border-bottom: var(--color-primary-disabled-border) 2px dashed;
  border-top: var(--color-primary-disabled-border) 2px dashed;
}

.subtitle {
  font-weight: bold;
  text-align: left;
}

.items {
  overflow: hidden;
  transition: 580ms ease-in-out max-height;

  &:global(.slide-enter),
  &:global(.slide-leave-to) {
    max-height: 0;
  }

  &:global(.slide-enter-to),
  &:global(.slide-leave) {
    max-height: 100vh;
  }
}

.flexrow {
  display: flex;
  justify-content: space-between;

  h4:first-child {
    text-align: left;
  }
}

.row {
  margin: 8px 0;
}

.bold {
  h4 {
    font-weight: bold;
  }
}

.cross {
  margin-left: 16px;
  background: var(--color-primary);
  height: 18px;
  position: relative;
  width: 2px;
  transition: 300ms ease-in-out transform;

  &:after {
    transition: 300ms ease-in-out transform;
    background: var(--color-primary);
    content: '';
    height: 2px;
    left: -8px;
    position: absolute;
    top: 8px;
    width: 18px;
  }
}

.open {
  &.cross {
    transform: rotate(135deg);
    :after {
      transform: rotate(225deg);
    }
  }
}
</style>
